import { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getAnnouncement, createAnnouncement, deleteAnnouncement, uploadImage } from "@/api/endpoints";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ImageCropDialog } from "@/components/image-crop-dialog";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Megaphone, Upload, Trash2, Plus, X } from "lucide-react";

const ALL_LANGS = [
  { code: "tr", flag: "🇹🇷" }, { code: "en", flag: "🇬🇧" }, { code: "de", flag: "🇩🇪" },
  { code: "fr", flag: "🇫🇷" }, { code: "es", flag: "🇪🇸" }, { code: "ar", flag: "🇸🇦" },
  { code: "ru", flag: "🇷🇺" }, { code: "zh", flag: "🇨🇳" },
];

export function AnnouncementPage() {
  const { venueId } = useParams<{ venueId: string }>();
  const qc = useQueryClient();
  const { t } = useI18n();
  const fileRef = useRef<HTMLInputElement>(null);

  const [image, setImage] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [titleTranslations, setTitleTranslations] = useState<Record<string, string>>({});
  const [descTranslations, setDescTranslations] = useState<Record<string, string>>({});
  const [transLangs, setTransLangs] = useState<string[]>([]);
  const [langPickerOpen, setLangPickerOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState("");
  const [showCrop, setShowCrop] = useState(false);
  const [uploading, setUploading] = useState(false);

  const { data: announcement, isLoading } = useQuery({
    queryKey: ["announcement", venueId],
    queryFn: () => getAnnouncement(venueId!),
    enabled: !!venueId,
  });

  // Populate form from existing announcement
  useEffect(() => {
    if (announcement) {
      setImage(announcement.image);
      setTitle(announcement.title || "");
      setDescription(announcement.description || "");
      setTitleTranslations(announcement.titleTranslations || {});
      setDescTranslations(announcement.descTranslations || {});
      setTransLangs(Object.keys({ ...announcement.titleTranslations, ...announcement.descTranslations }));
    }
  }, [announcement]);

  const saveMutation = useMutation({
    mutationFn: () => createAnnouncement(venueId!, {
      image,
      title: title || undefined,
      titleTranslations: Object.keys(titleTranslations).length ? titleTranslations : undefined,
      description: description || undefined,
      descTranslations: Object.keys(descTranslations).length ? descTranslations : undefined,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["announcement", venueId] }),
  });

  const removeMutation = useMutation({
    mutationFn: () => deleteAnnouncement(venueId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["announcement", venueId] });
      setImage("");
      setTitle("");
      setDescription("");
      setTitleTranslations({});
      setDescTranslations({});
      setTransLangs([]);
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { setCropSrc(reader.result as string); setShowCrop(true); };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleCropped = async (file: File) => {
    setUploading(true);
    try {
      const url = await uploadImage(file, "announcement" as "item", venueId!);
      setImage(url);
    } finally {
      setUploading(false);
    }
  };

  if (isLoading) return <div className="py-8 text-center text-muted-foreground">{t("loading")}</div>;

  return (
    <div className="max-w-2xl">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("announcement")}</h1>
        {announcement && (
          <ConfirmDialog
            trigger={<Button variant="destructive" size="sm"><Trash2 className="mr-1 size-4" />{t("delete_announcement")}</Button>}
            title={t("delete_announcement")}
            description={t("announcement_desc")}
            confirmLabel={t("delete")}
            onConfirm={() => removeMutation.mutate()}
          />
        )}
      </div>

      <p className="mb-6 text-sm text-muted-foreground">{t("announcement_desc")}</p>

      {/* Current announcement */}
      {announcement && !image && (
        <Card className="mb-6">
          <CardContent className="pt-5">
            <div className="flex gap-4">
              <img src={announcement.image} alt="" className="h-40 w-28 rounded-lg object-cover" />
              <div>
                {announcement.title && <p className="font-semibold">{announcement.title}</p>}
                {announcement.description && <p className="text-sm text-muted-foreground mt-1">{announcement.description}</p>}
                <p className="mt-2 text-xs text-green-600 font-medium">Aktif</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Megaphone className="size-5" />
            {announcement ? t("edit") : t("create_announcement")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={(e) => { e.preventDefault(); if (image) saveMutation.mutate(); }} className="space-y-4">
            {/* Image */}
            <div>
              <Label>{t("announcement_image")} <span className="text-destructive">*</span></Label>
              <div className="mt-2 space-y-3">
                {image && <img src={image} alt="" className="h-48 w-32 rounded-lg object-cover" />}
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} />
                <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}>
                  {uploading ? (
                    <><svg className="mr-2 size-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>{t("uploading")}</>
                  ) : (
                    <><Upload className="mr-2 size-4" />{t("upload")}</>
                  )}
                </Button>
              </div>
              <ImageCropDialog open={showCrop} onClose={() => { setShowCrop(false); setCropSrc(""); }} imageSrc={cropSrc} aspectRatio={9 / 16} onCropped={handleCropped} />
            </div>

            {/* Title & Description */}
            <div>
              <Label>{t("item_name")} ({t("general")})</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Opsiyonel" />
            </div>
            <div>
              <Label>{t("description")} ({t("general")})</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Opsiyonel" />
            </div>

            {/* Translations */}
            {transLangs.length > 0 && (
              <div className="rounded-lg border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="w-10"></th>
                      <th className="px-2 py-2 text-left font-medium text-xs">{t("item_name")}</th>
                      <th className="px-2 py-2 text-left font-medium text-xs">{t("description")}</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {transLangs.map((code) => {
                      const lang = ALL_LANGS.find(l => l.code === code);
                      return (
                        <tr key={code} className="border-b last:border-0">
                          <td className="px-3 py-1.5 text-center text-lg">{lang?.flag || "🌐"}</td>
                          <td className="px-2 py-1.5">
                            <Input className="h-8 text-sm" placeholder={title} value={titleTranslations[code] || ""} onChange={(e) => setTitleTranslations({ ...titleTranslations, [code]: e.target.value })} />
                          </td>
                          <td className="px-2 py-1.5">
                            <Input className="h-8 text-sm" placeholder={description} value={descTranslations[code] || ""} onChange={(e) => setDescTranslations({ ...descTranslations, [code]: e.target.value })} />
                          </td>
                          <td className="px-2 py-1.5">
                            <button type="button" className="text-muted-foreground hover:text-destructive" onClick={() => {
                              setTransLangs(transLangs.filter(l => l !== code));
                              const tt = { ...titleTranslations }; delete tt[code]; setTitleTranslations(tt);
                              const dt = { ...descTranslations }; delete dt[code]; setDescTranslations(dt);
                            }}><X className="size-4" /></button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <Button type="button" variant="outline" size="sm" onClick={() => setLangPickerOpen(true)}>
              <Plus className="mr-1 size-4" /> {t("add")} {t("language")}
            </Button>
            {langPickerOpen && (
              <>
                <div className="fixed inset-0 z-[100] bg-black/50" onClick={() => setLangPickerOpen(false)} />
                <div className="fixed left-1/2 top-1/2 z-[101] w-72 -translate-x-1/2 -translate-y-1/2 rounded-xl border bg-card shadow-2xl">
                  <div className="border-b px-4 py-3 font-semibold text-sm">{t("add")} {t("language")}</div>
                  <div className="max-h-48 overflow-y-auto">
                    {ALL_LANGS.filter(l => !transLangs.includes(l.code)).map(l => (
                      <button key={l.code} type="button" className="flex w-full items-center gap-3 px-4 py-2.5 text-sm hover:bg-muted" onClick={() => { setTransLangs([...transLangs, l.code]); setLangPickerOpen(false); }}>
                        <span className="text-lg">{l.flag}</span>
                        <span>{l.code.toUpperCase()}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            <Button type="submit" className="w-full" disabled={!image || saveMutation.isPending}>
              {saveMutation.isPending ? t("saving") : t("save")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
